const getFatigueData = day => {
  if (!day.fatigueReports) return;
  const fatigueData = day.fatigueReports.flatMap((f, i) => {
    const x = f.time[0];
    if (f.fatigue) {
      const y = f.fatigue;
      return { x: x, y: y };
    } else {
      const y = i > 0 && day.fatigueReports ? day.fatigueReports[i - 1].fatigue || null : null;
      return [
        { x: x, y: y },
        { x: x, y: null },
      ];
    }
  });
  fatigueData.push({
    x: day.fatigueReports[day.fatigueReports.length - 1].time[0] + 2,
    y: day.fatigueReports[day.fatigueReports.length - 1].fatigue || null,
  });
  return fatigueData;
};

const getSleepData = day => {
  if (!day.sleep || !day.sleep.start || !day.sleep.end) return;
  const quality = day.sleepQuality ? day.sleepQuality : 10;
  const sleepData = [
    {
      x:
        day.sleep.start[0] +
        day.sleep.start[1] / 60 -
        (day.sleep.start[0] + day.sleep.start[1] / 60 > day.sleep.end[0] + day.sleep.end[1] / 60 ? 24 : 0),
      y: -20,
      quality: quality,
    },
    { x: day.sleep.end[0] + day.sleep.end[1] / 60, y: -20, quality: quality },
  ];
  return sleepData;
};

const getPhysicalLoadData = day => {
  if (!day.fatigueReports) return;
  const physicalData = day.fatigueReports.map(f => {
    if (!f.time) return null;
    const y = -40;
    const x1 = f.time[0] + f.time[1] / 60;
    const x2 = x1 + 2;
    const value = f.physicalStrain;
    return [
      { x: x1, y: y, value: value },
      { x: x2, y: y, value: value },
    ];
  });
  return physicalData;
};

const getCognitiveLoadData = day => {
  if (!day.fatigueReports) return;
  const cognitiveData = day.fatigueReports.map(f => {
    if (!f.time) return null;
    const y = -70;
    const x1 = f.time[0] + f.time[1] / 60;
    const x2 = x1 + 2;
    const value = f.cognitiveLoad;
    return [
      { x: x1, y: y, value: value },
      { x: x2, y: y, value: value },
    ];
  });
  return cognitiveData.filter(c => c);
};

const getMealData = day => {
  if (!day.meals) return;
  const mealData = day.meals.map(m => {
    if (!m.time) return null;
    const y = -20;
    const x = m.time[0] + m.time[1] / 60;
    return { x: x, y: y };
  });
  return mealData.filter(m => m);
};

const getRestData = day => {
  if (!day.restBreaks) return;
  const restData = day.restBreaks.map(r => {
    if (!r.startTime) return null;
    const y = -20;
    const x = r.startTime[0] + r.startTime[1] / 60;
    return { x: x, y: y };
  });
  return restData.filter(r => r);
};

const getNumberValuesFromReports = (diaryDay, reportType, valueType) => {
  const reports = diaryDay[reportType];
  return Array.isArray(reports) ? reports.map(r => (r ? r[valueType] : undefined)).filter(v => v || v === 0) : [];
};

const getMinAverageMaxFromNumberArray = (values, decimalCount) => {
  if (Array.isArray(values) && values.length > 0) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    let count = 0;
    values.forEach(v => (count += v || 0));
    const avg = count / values.length;
    const dCount = decimalCount || 0;
    return [min.toFixed(dCount), avg.toFixed(dCount), max.toFixed(dCount)].map(v => v.replace('.', ','));
  }
  return ['', '', ''];
};

const getDiaryDailyEventCount = diaryDay => {
  const events = diaryDay;
  let count = 0;
  events && events.forEach(() => count++);
  return count;
};

const getDiaryDailyMinAverageMax = (diaryDay, reportType, valueType, decimalCount) => {
  const values = getNumberValuesFromReports(diaryDay, reportType, valueType);
  return getMinAverageMaxFromNumberArray(values, decimalCount);
};

const getDiaryWeeklyMinAverageMax = (diaryWeek, eventType, reportType, decimalCount, sleepQuality) => {
  if (Array.isArray(diaryWeek) && diaryWeek.length > 0 && eventType) {
    switch (eventType) {
      case 'meals':
        const values = diaryWeek.map(diaryDay => getDiaryDailyEventCount(getMealData(diaryDay)));
        return getMinAverageMaxFromNumberArray(values, decimalCount);
      case 'restBreaks': {
        const values = diaryWeek.map(diaryDay => getDiaryDailyEventCount(getRestData(diaryDay)));
        return getMinAverageMaxFromNumberArray(values, decimalCount);
      }
      case 'sleep': {
        const values = diaryWeek
          .map(diaryDay =>
            getSleepData(diaryDay)
              ? (getSleepData(diaryDay)[1].x || 0) - (getSleepData(diaryDay)[0].x || 0)
              : undefined,
          )
          .filter(v => v || v === 0);
        const sleepSet = getMinAverageMaxFromNumberArray(values, decimalCount || 2);
        const sleepQualities = diaryWeek
          .map(diaryDay => (getSleepData(diaryDay) ? getSleepData(diaryDay)[0].quality || 0 : undefined))
          .filter(v => v || v === 0);
        const sleepQualitySet = getMinAverageMaxFromNumberArray(sleepQualities, decimalCount);
        if (sleepQuality) sleepQualitySet.map(sq => sleepSet.push(sq));
        return sleepSet;
      }
      case 'fatigueReports': {
        if (!reportType) break;
        const weekFatigueRepNumbers = diaryWeek.flatMap(d =>
          getNumberValuesFromReports(d, 'fatigueReports', reportType),
        );
        return getMinAverageMaxFromNumberArray(weekFatigueRepNumbers, decimalCount);
      }
    }
  }
  return ['', '', ''];
};

export {
  getFatigueData,
  getSleepData,
  getPhysicalLoadData,
  getCognitiveLoadData,
  getMealData,
  getRestData,
  getDiaryDailyEventCount,
  getDiaryDailyMinAverageMax,
  getDiaryWeeklyMinAverageMax,
};
